/**
 * useActiveAssignments — Single source of truth for "is there work
 * waiting for this learner?".
 *
 * Audit ref: LEARNER_HOME_FLOW_UX_AUDIT_2026 §P1-3.
 *
 * Before this hook, the same `Promise.all([getTeacherAssignments, getPracticeAssignments])`
 * filter pipeline was inlined in three places:
 *   - HomePage.tsx          (banner rendering, group-filtered)
 *   - Sidebar.tsx           (oranje dot op het Toetsen nav-item)
 *   - MobileNav.tsx         (zelfde dot, mobile)
 *
 * Each maintained its own `now` check, `completedBy` filter and `wholeClass`
 * logic — drift-risico bij elke regel-change. This hook consolidates the
 * pipeline; callers filter further by group if needed.
 *
 * Note: this is a one-shot fetch, no polling. It refetches when the
 * `learnerId` changes (account-switch) and exposes a `revalidate()` for
 * callers that want to refresh after a known mutation (e.g. completing an
 * assignment elsewhere in the session).
 */

import { useCallback, useEffect, useState } from 'react'
import { dataLayer } from '../../data/dataLayer'
import type { TeacherAssignment, PracticeAssignment } from '../../assessment'

export interface ActiveAssignments {
  /** All active teacher-assessment assignments for this learner (any group). */
  assessments: TeacherAssignment[]
  /** All active practice assignments for this learner (any group). */
  practiceAssignments: PracticeAssignment[]
  /** Convenience: true if either list has at least one entry. */
  hasAny: boolean
  /** True while the first fetch is in flight. */
  isLoading: boolean
  /** Force a refetch (e.g. after the learner completes work elsewhere). */
  revalidate: () => void
}

const EMPTY: Pick<
  ActiveAssignments,
  'assessments' | 'practiceAssignments' | 'hasAny'
> = {
  assessments: [],
  practiceAssignments: [],
  hasAny: false,
}

function isActiveFor(
  assignment: {
    deadline?: string | number | Date | null
    completedByStudentIds?: string[]
    wholeClass?: boolean
    targetStudentIds?: string[]
  },
  learnerId: string,
  now: Date,
): boolean {
  if (assignment.deadline && new Date(assignment.deadline) < now) return false
  if (assignment.completedByStudentIds?.includes(learnerId)) return false
  if (assignment.wholeClass) return true
  if (assignment.targetStudentIds?.includes(learnerId)) return true
  return false
}

export function useActiveAssignments(
  learnerId: string | null | undefined,
): ActiveAssignments {
  const [data, setData] =
    useState<
      Pick<ActiveAssignments, 'assessments' | 'practiceAssignments' | 'hasAny'>
    >(EMPTY)
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(learnerId))
  const [version, setVersion] = useState(0)

  const revalidate = useCallback(() => setVersion((v) => v + 1), [])

  useEffect(() => {
    if (!learnerId) {
      setData(EMPTY)
      setIsLoading(false)
      return
    }
    let cancelled = false
    setIsLoading(true)
    Promise.all([
      dataLayer.getTeacherAssignments(),
      dataLayer.getPracticeAssignments(),
    ])
      .then(([allAssessments, allPractice]) => {
        if (cancelled) return
        const now = new Date()
        const assessments = Object.values(allAssessments).filter((a) =>
          isActiveFor(a, learnerId, now),
        )
        const practiceAssignments = Object.values(allPractice).filter((a) =>
          isActiveFor(a, learnerId, now),
        )
        setData({
          assessments,
          practiceAssignments,
          hasAny: assessments.length > 0 || practiceAssignments.length > 0,
        })
      })
      .catch(() => {
        if (!cancelled) setData(EMPTY)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [learnerId, version])

  return { ...data, isLoading, revalidate }
}
